import { useState, useCallback, useMemo, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { FolderPlus } from 'lucide-react';
import { createCategory, updateMenuCategory, type FetchMenuResponse, type MenuCategory } from '@/lib/api/menu';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from 'react-i18next';
import { AvailabilityEditor } from '@/features/restaurant/components/menu/AvailabilityEditor';
import type { MenuAvailabilityRules } from '@/types/menuAvailability';

interface AddCategoryModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tenantSlug?: string;
  existingCategories: { id: string; name: string }[];
  timezone?: string | null;
  initialCategory?: MenuCategory | null;
}

const EMPTY_AVAILABILITY_RULES: MenuAvailabilityRules = { dayparts: [], holidays: [], limitedRuns: [] } as const;

const cloneAvailabilityRules = (rules?: MenuAvailabilityRules | null): MenuAvailabilityRules => ({
  timezone: rules?.timezone,
  dayparts: (rules?.dayparts ?? []).map((entry) => ({ ...entry, days: [...entry.days] })),
  holidays: (rules?.holidays ?? []).map((entry) => ({ ...entry })),
  limitedRuns: (rules?.limitedRuns ?? []).map((entry) => ({ ...entry })),
});

export function AddCategoryModal({
  open,
  onOpenChange,
  tenantSlug,
  existingCategories,
  timezone,
  initialCategory,
}: AddCategoryModalProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { t } = useTranslation('menu');

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [spanishName, setSpanishName] = useState('');
  const [spanishDescription, setSpanishDescription] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [availabilityRules, setAvailabilityRules] = useState<MenuAvailabilityRules>(cloneAvailabilityRules(EMPTY_AVAILABILITY_RULES));
  const [autoExpireAtInput, setAutoExpireAtInput] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isEditMode = Boolean(initialCategory);

  const updateCategoryInCaches = useCallback(
    (categoryId: string, modifier: (category: MenuCategory) => MenuCategory) => {
      const applyUpdate = (snapshot: FetchMenuResponse | undefined): FetchMenuResponse | undefined => {
        if (!snapshot) {
          return snapshot;
        }
        let didUpdate = false;
        const categories = snapshot.categories.map((category) => {
          if (category.id !== categoryId) {
            return category;
          }
          didUpdate = true;
          return modifier(category);
        });
        return didUpdate ? { ...snapshot, categories } : snapshot;
      };

      queryClient.setQueriesData<FetchMenuResponse | undefined>({ queryKey: ['menu'] }, applyUpdate);
      queryClient.setQueriesData<FetchMenuResponse | undefined>({ queryKey: ['menu', 'admin'] }, applyUpdate);
    },
    [queryClient],
  );

  useEffect(() => {
    if (!open) {
      return;
    }
    setName(initialCategory?.name ?? '');
    setDescription('');
    const esTranslation = initialCategory?.translations?.es ?? {};
    setSpanishName(typeof esTranslation?.name === 'string' ? esTranslation.name : '');
    setSpanishDescription(typeof esTranslation?.description === 'string' ? String(esTranslation.description) : '');
    setIsActive(initialCategory?.isActive ?? true);
    setAvailabilityRules(cloneAvailabilityRules(initialCategory?.availabilityRules ?? EMPTY_AVAILABILITY_RULES));
    setAutoExpireAtInput(initialCategory?.autoExpireAt ? new Date(initialCategory.autoExpireAt).toISOString().slice(0, 16) : '');
    setErrors({});
    setIsSubmitting(false);
  }, [initialCategory, open]);

  const suggestedCategories = useMemo<string[]>(() => {
    const suggestions = t('modals.addCategory.quickAdd.suggestions', { returnObjects: true });
    return Array.isArray(suggestions)
      ? suggestions.filter((value): value is string => typeof value === 'string')
      : [];
  }, [t]);

  const validateForm = useCallback(() => {
    const nextErrors: Record<string, string> = {};
    const trimmedName = name.trim();
    const trimmedSpanishName = spanishName.trim();
    const trimmedSpanishDescription = spanishDescription.trim();

    if (!trimmedName) {
      nextErrors.name = t('modals.addCategory.validation.nameRequired');
    } else if (trimmedName.length < 2) {
      nextErrors.name = t('modals.addCategory.validation.nameLength');
    } else {
      const duplicate = existingCategories.some((category) => {
        if (initialCategory && category.id === initialCategory.id) {
          return false;
        }
        return category.name.toLowerCase() === trimmedName.toLowerCase();
      });
      if (duplicate) {
        nextErrors.name = t('modals.addCategory.validation.duplicate');
      }
    }

    if (!trimmedSpanishName) {
      nextErrors.spanishName = t('modals.addCategory.validation.spanishNameRequired', {
        defaultValue: 'Spanish name is required',
      });
    }

    if (!trimmedSpanishDescription) {
      nextErrors.spanishDescription = t('modals.addCategory.validation.spanishDescriptionRequired', {
        defaultValue: 'Spanish description is required',
      });
    }

    if (autoExpireAtInput) {
      const parsed = Date.parse(autoExpireAtInput);
      if (Number.isNaN(parsed)) {
        nextErrors.autoExpireAt = t('modals.addCategory.validation.autoExpireInvalid', {
          defaultValue: 'Select a valid auto-expiration time.',
        });
      }
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  }, [autoExpireAtInput, existingCategories, initialCategory, name, spanishDescription, spanishName, t]);

  const handleSubmit = useCallback(async (event: React.FormEvent) => {
    event.preventDefault();
    if (!tenantSlug) {
      toast({
        title: t('modals.addCategory.validation.tenantRequired', { defaultValue: 'Select a restaurant first.' }),
        variant: 'destructive',
      });
      return;
    }

    if (!validateForm()) {
      return;
    }

    const trimmedName = name.trim();
    const trimmedSpanishName = spanishName.trim();
    const trimmedSpanishDescription = spanishDescription.trim();
    const autoExpireAt = autoExpireAtInput ? Date.parse(autoExpireAtInput) : null;
    const payloadRules = cloneAvailabilityRules(availabilityRules);

    try {
      setIsSubmitting(true);
      const normalizedAutoExpire =
        autoExpireAt && Number.isFinite(autoExpireAt) ? autoExpireAt : null;

      const translationsPayload = {
        es: {
          name: trimmedSpanishName,
          description: trimmedSpanishDescription || null,
        },
      };

      if (initialCategory) {
        await updateMenuCategory(tenantSlug, initialCategory.id, {
          name: trimmedName,
          isActive,
          availabilityRules: payloadRules,
          autoExpireAt: normalizedAutoExpire,
          translations: translationsPayload,
        });

        updateCategoryInCaches(initialCategory.id, (category) => ({
          ...category,
          name: trimmedName,
          isActive,
          availabilityRules: cloneAvailabilityRules(payloadRules),
          autoExpireAt: normalizedAutoExpire,
          translations: {
            ...(category.translations ?? {}),
            es: {
              ...(category.translations?.es ?? {}),
              name: trimmedSpanishName,
              description: trimmedSpanishDescription || null,
            },
          },
        }));

        toast({
          title: t('toasts.categoryUpdated.title', { defaultValue: 'Category updated' }),
          description: t('toasts.categoryUpdated.description', {
            defaultValue: '{{name}} schedule saved.',
            name: trimmedName,
          }),
        });
      } else {
        await createCategory(tenantSlug, {
          name: trimmedName,
          isActive,
          availabilityRules: payloadRules,
          autoExpireAt: normalizedAutoExpire,
          translations: translationsPayload,
        });
        toast({
          title: t('toasts.categoryCreated.title'),
          description: t('toasts.categoryCreated.description', { name: trimmedName }),
        });
      }

      await queryClient.invalidateQueries({ queryKey: ['menu'] });
      await queryClient.invalidateQueries({ queryKey: ['menuMetrics'] });
      onOpenChange(false);
    } catch (error) {
      console.error('[AddCategoryModal] submit failed', error);
      toast({
        title: isEditMode
          ? t('toasts.categoryUpdateError.title', { defaultValue: 'Unable to update category' })
          : t('toasts.categoryCreateError.title', { defaultValue: 'Unable to create category' }),
        description:
          error instanceof Error
            ? error.message
            : t('toasts.categoryCreateError.description', { defaultValue: 'Please try again.' }),
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [
    availabilityRules,
    autoExpireAtInput,
    initialCategory,
    isActive,
    isEditMode,
    name,
    spanishName,
    spanishDescription,
    onOpenChange,
    queryClient,
    t,
    tenantSlug,
    toast,
    updateCategoryInCaches,
    validateForm,
  ]);

  const handleSuggestedCategoryClick = useCallback((suggestedName: string) => {
    setName(suggestedName);
  }, []);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          onOpenChange(false);
        } else {
          onOpenChange(true);
        }
      }}
    >
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <FolderPlus className="w-5 h-5 text-primary" />
            {isEditMode
              ? t('modals.editCategory.title', { defaultValue: 'Edit category' })
              : t('modals.addCategory.title')}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          <Card>
            <CardContent className="pt-6 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="category-name" className="text-sm font-medium">
                  {t('modals.addCategory.labels.name')}
                </Label>
                <Input
                  id="category-name"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  placeholder={t('modals.addCategory.placeholders.name')}
                  className={errors.name ? 'border-destructive' : ''}
                />
                {errors.name ? <p className="text-xs text-destructive">{errors.name}</p> : null}
              </div>

              <div className="space-y-2">
                <Label htmlFor="category-name-es" className="text-sm font-medium">
                  {t('modals.addCategory.labels.spanishName', { defaultValue: 'Nombre (Español)' })}
                </Label>
                <Input
                  id="category-name-es"
                  value={spanishName}
                  onChange={(event) => setSpanishName(event.target.value)}
                  placeholder={t('modals.addCategory.placeholders.spanishName', {
                    defaultValue: 'Nombre en español',
                  })}
                  className={errors.spanishName ? 'border-destructive' : ''}
                />
                {errors.spanishName ? <p className="text-xs text-destructive">{errors.spanishName}</p> : null}
              </div>

              <div className="space-y-2">
                <Label htmlFor="category-description" className="text-sm font-medium">
                  {t('modals.addCategory.labels.description')}
                </Label>
                <Input
                  id="category-description"
                  value={description}
                  onChange={(event) => setDescription(event.target.value)}
                  placeholder={t('modals.addCategory.placeholders.description')}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="category-description-es" className="text-sm font-medium">
                  {t('modals.addCategory.labels.spanishDescription', { defaultValue: 'Descripción (Español)' })}
                </Label>
                <Input
                  id="category-description-es"
                  value={spanishDescription}
                  onChange={(event) => setSpanishDescription(event.target.value)}
                  placeholder={t('modals.addCategory.placeholders.spanishDescription', {
                    defaultValue: 'Describe la categoría en español...',
                  })}
                  className={errors.spanishDescription ? 'border-destructive' : ''}
                />
                {errors.spanishDescription ? (
                  <p className="text-xs text-destructive">{errors.spanishDescription}</p>
                ) : null}
              </div>

              <div className="flex items-center justify-between rounded-md border border-border/60 bg-muted/30 px-3 py-2">
                <div>
                  <p className="text-sm font-medium">{t('modals.addCategory.labels.status', { defaultValue: 'Active' })}</p>
                  <p className="text-xs text-muted-foreground">
                    {t('modals.addCategory.helpers.status', { defaultValue: 'Inactive categories will stay hidden from guests.' })}
                  </p>
                </div>
                <Switch checked={isActive} onCheckedChange={setIsActive} />
              </div>

              <div className="space-y-2">
                <Label htmlFor="category-auto-expire" className="text-sm font-medium">
                  {t('modals.addCategory.labels.autoExpire', { defaultValue: 'Auto-expire at (optional)' })}
                </Label>
                <Input
                  id="category-auto-expire"
                  type="datetime-local"
                  value={autoExpireAtInput}
                  onChange={(event) => setAutoExpireAtInput(event.target.value)}
                />
                {errors.autoExpireAt ? <p className="text-xs text-destructive">{errors.autoExpireAt}</p> : null}
              </div>
            </CardContent>
          </Card>

          {suggestedCategories.length > 0 && !isEditMode ? (
            <Card>
              <CardContent className="pt-6">
                <Label className="text-sm font-medium mb-3 block">
                  {t('modals.addCategory.quickAdd.title')}
                </Label>
                <div className="flex flex-wrap gap-2">
                  {suggestedCategories
                    .filter((suggested) =>
                      !existingCategories.some((existing) => existing.name.toLowerCase() === suggested.toLowerCase()),
                    )
                    .map((suggested) => (
                      <Button
                        key={suggested}
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => handleSuggestedCategoryClick(suggested)}
                        className="text-xs"
                      >
                        {suggested}
                      </Button>
                    ))}
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  {t('modals.addCategory.quickAdd.hint')}
                </p>
              </CardContent>
            </Card>
          ) : null}

          <Card>
            <CardContent className="pt-6 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold">{t('modals.addCategory.labels.schedule', { defaultValue: 'Availability schedule' })}</p>
                  <p className="text-xs text-muted-foreground">
                    {t('modals.addCategory.helpers.schedule', {
                      defaultValue: 'Control when this category appears for guests across all channels.',
                    })}
                  </p>
                </div>
              </div>
              <AvailabilityEditor value={availabilityRules} onChange={setAvailabilityRules} timezone={timezone} />
            </CardContent>
          </Card>

          <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:justify-end">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
              {t('modals.addCategory.actions.cancel', { defaultValue: 'Cancel' })}
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <span className="flex items-center gap-2">
                  <span className="h-3 w-3 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                  {t('modals.addCategory.actions.saving', { defaultValue: 'Saving…' })}
                </span>
              ) : (
                isEditMode
                  ? t('modals.editCategory.actions.save', { defaultValue: 'Save changes' })
                  : t('modals.addCategory.actions.create', { defaultValue: 'Create category' })
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
